import React from "react";
import {
  Award,
  DollarSign,
  Hammer,
  MessageSquareQuote,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import {
  CTASection,
  DifferentFromFsmSection,
  PricingSection,
  TestimonialsSection,
  TradesSection,
  TrustFeaturesBar,
  WhyToolKitSection,
} from "../landing";
import type { RouteMap, TradeContent } from "../../types";

/**
 * Identifier for each top-level landing-section page. Mirrors the
 * `MainPage` union in `../../types`.
 */
export type LandingSectionSlug =
  | "features"
  | "pricing"
  | "why-toolkit"
  | "trades"
  | "testimonials"
  | "different";

export type LandingSectionContext = {
  routes: RouteMap;
  trades?: TradeContent[];
  scrollToSection: (id: string) => void;
};

export type LandingSectionDefinition = {
  slug: LandingSectionSlug;
  href: string;
  navLabel: string;
  kicker: string;
  kickerIcon: LucideIcon;
  heroH1: string;
  heroSubhead: string;
  primaryCtaLabel: string;
  primaryCtaHref: (routes: RouteMap) => string;
  /**
   * Whether the page should also render the homepage CTASection / additional
   * marketing sections after the hero. Most pages do; testimonials / different
   * already include their own framing so we keep them lean.
   */
  appendCtaSection: boolean;
  /**
   * Render the actual content section (the React component shared with the
   * homepage anchor). Receives shared context (routes, scroll handler, trades).
   */
  renderContent: (ctx: LandingSectionContext) => React.ReactNode;
};

const buildSignupHref = (routes: RouteMap): string => routes.signup;

export const LANDING_SECTION_DEFINITIONS: Record<
  LandingSectionSlug,
  LandingSectionDefinition
> = {
  features: {
    slug: "features",
    href: "/features",
    navLabel: "Features",
    kicker: "Features",
    kickerIcon: Sparkles,
    heroH1: "Get found. Take quotes. Keep licenses current.",
    heroSubhead:
      "Chat for a minute. Walk away with a live site, job photos, and reminders.",
    primaryCtaLabel: "Start 30-day trial",
    primaryCtaHref: buildSignupHref,
    appendCtaSection: true,
    renderContent: () => (
      <div className="bg-background pt-12 pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <TrustFeaturesBar />
        </div>
      </div>
    ),
  },
  pricing: {
    slug: "pricing",
    href: "/pricing",
    navLabel: "Pricing",
    kicker: "Pricing",
    kickerIcon: DollarSign,
    heroH1: "$9/month after trial",
    heroSubhead:
      "30 days free. Then one bill for the website, job photos, and licenses. Cancel anytime.",
    primaryCtaLabel: "Start 30-day trial",
    primaryCtaHref: buildSignupHref,
    appendCtaSection: false,
    renderContent: ({ routes }) => <PricingSection routes={routes} />,
  },
  "why-toolkit": {
    slug: "why-toolkit",
    href: "/why-toolkit",
    navLabel: "Why ToolKit",
    kicker: "Why ToolKit",
    kickerIcon: ShieldCheck,
    heroH1: "Built for sole trades and small crews",
    heroSubhead:
      "Chat for a minute. Get found, take quotes, keep photos and licenses in one place. $9/month after trial.",
    primaryCtaLabel: "Start 30-day trial",
    primaryCtaHref: buildSignupHref,
    appendCtaSection: true,
    renderContent: ({ scrollToSection }) => (
      <WhyToolKitSection scrollToSection={scrollToSection} />
    ),
  },
  trades: {
    slug: "trades",
    href: "/trades",
    navLabel: "Trades",
    kicker: "Built for every trade",
    kickerIcon: Hammer,
    heroH1: "Every trade. Same $9.",
    heroSubhead:
      "Plumbers, electricians, HVAC techs, landscapers, painters, and general contractors. A site, job photos, and licenses that match the work you do.",
    primaryCtaLabel: "Start 30-day trial",
    primaryCtaHref: buildSignupHref,
    appendCtaSection: true,
    renderContent: ({ trades }) => <TradesSection trades={trades ?? []} />,
  },
  testimonials: {
    slug: "testimonials",
    href: "/testimonials",
    navLabel: "Testimonials",
    kicker: "Customer stories",
    kickerIcon: MessageSquareQuote,
    heroH1: "From people in the trade",
    heroSubhead:
      "Plumbers, electricians, and general contractors who use ToolKit to get found, take quotes, and keep photos and licenses in one place.",
    primaryCtaLabel: "Start 30-day trial",
    primaryCtaHref: buildSignupHref,
    appendCtaSection: true,
    renderContent: () => <TestimonialsSection />,
  },
  different: {
    slug: "different",
    href: "/different",
    navLabel: "How we're different",
    kicker: "How we're different",
    kickerIcon: Award,
    heroH1: "Don't buy dispatch software to get a website.",
    heroSubhead:
      "Housecall Pro and Jobber handle the calendar and the invoice. ToolKit is the public site, job photos, and license vault. Use both if you want.",
    primaryCtaLabel: "Start 30-day trial",
    primaryCtaHref: buildSignupHref,
    appendCtaSection: true,
    renderContent: () => <DifferentFromFsmSection />,
  },
};

/**
 * Static export so non-React modules (sitemap, header link lists, footer)
 * can iterate over the same source of truth without pulling in JSX.
 */
export const LANDING_SECTION_NAV_ENTRIES: ReadonlyArray<{
  slug: LandingSectionSlug;
  href: string;
  navLabel: string;
}> = (
  Object.values(LANDING_SECTION_DEFINITIONS) as LandingSectionDefinition[]
).map(({ slug, href, navLabel }) => ({ slug, href, navLabel }));

/**
 * Renders the homepage CTASection only when the page asks for it. Kept here
 * so the page component stays a dumb consumer of the config.
 */
export const renderLandingSectionTrailingCta = (
  definition: LandingSectionDefinition,
  routes: RouteMap,
): React.ReactNode =>
  definition.appendCtaSection ? <CTASection routes={routes} /> : null;
